// Converte uma mensagem de pagina Meta (Instagram Direct / Facebook Messenger),
// tanto vinda do banco quanto do socket, para o MESMO shape que o balao de mensagem
// do chat (ChatComponent) espera — o mesmo formato do WhatsApp: campos no topo
// (type, body, fromMe, t, mimetype, ...). Sem isso o balao renderiza vazio, porque
// as mensagens Meta guardam o texto em message.message.text e a direcao em message.isMe.
//
// Midia: as paginas Meta entregam anexos como { type, payload: { url } }. Adaptamos
// para os MESMOS type/mimetype do WhatsApp (image/video/ptt) e expomos a URL em
// mediaUrl + flag isMetaMedia, que o ChatComponent usa para renderizar a midia
// direto da URL (o WhatsApp usa base64/cache; o Instagram usa URL de CDN).
//
// Doc cru esperado:
// {
//   _id, id, instagramUserId|messengerUserId|userId, timestamp, date,
//   message: {
//     sender: { id }, recipient: { id }, timestamp,
//     message: { mid, text, attachments? [{ type, payload: { url } }], reply_to? },
//     isFile, isMe, reaction?, isRead?, is_deleted?,
//     quotedMsg?, quotedMsgId?  // resolvidos no webhook ao receber reply_to.mid
//   }
// }

// Mapeia o tipo de anexo Meta para o type/mimetype equivalente do WhatsApp.
const ATTACHMENT_TO_WHATSAPP = {
    image: { type: 'image', mimetype: 'image/jpeg' },
    video: { type: 'video', mimetype: 'video/mp4' },
    audio: { type: 'ptt', mimetype: 'audio/ogg' },
    file: { type: 'document', mimetype: 'application/octet-stream' },
};

// story_mention, share, reel etc. nao tem entrada no mapa acima — a Meta os
// rotula fora do vocabulario de midia, mas o conteudo por tras costuma ser uma
// midia normal (o cliente manda um video/foto do story). O lais-webhook
// (persistMetaMediaToS3) baixa QUALQUER anexo, sniffa o formato real por
// Content-Type/magic-bytes e re-hospeda no S3 com a extensao certa, sem nunca
// reescrever o `type` original no Mongo — entao aqui so a extensao da URL final
// diz se e midia de verdade. Tipos como "share" que apontam pra uma pagina (sem
// extensao reconhecida) continuam caindo no link de texto mais abaixo.
const EXT_TO_MEDIA = {
    jpg: { type: 'image', mimetype: 'image/jpeg' },
    jpeg: { type: 'image', mimetype: 'image/jpeg' },
    png: { type: 'image', mimetype: 'image/png' },
    gif: { type: 'image', mimetype: 'image/gif' },
    webp: { type: 'image', mimetype: 'image/webp' },
    mp4: { type: 'video', mimetype: 'video/mp4' },
    mov: { type: 'video', mimetype: 'video/quicktime' },
    webm: { type: 'video', mimetype: 'video/webm' },
    ogv: { type: 'video', mimetype: 'video/ogg' },
    m4a: { type: 'ptt', mimetype: 'audio/mp4' },
    mp3: { type: 'ptt', mimetype: 'audio/mpeg' },
    aac: { type: 'ptt', mimetype: 'audio/aac' },
    ogg: { type: 'ptt', mimetype: 'audio/ogg' },
    wav: { type: 'ptt', mimetype: 'audio/wav' },
};

function mediaFromUrlExtension(url) {
    if (!url) return null;
    const semQuery = url.split('?')[0].split('#')[0];
    const match = /\.([a-z0-9]+)$/i.exec(semQuery);
    return match ? EXT_TO_MEDIA[match[1].toLowerCase()] || null : null;
}

function buildQuotedFromReplyTo(core, outer) {
    // Resposta a story: o Instagram entrega o story respondido em reply_to.story.
    const story = core.reply_to?.story;
    if (story?.url) {
        return {
            quotedMsgId: story.id,
            quotedMsg: { type: 'image', isStory: true, mediaUrl: story.url, body: '', caption: '' }
        };
    }

    // Resposta a mensagem no Direct: reply_to.mid (+ is_self_reply).
    // Preferir quotedMsg ja resolvido no webhook; senao placeholder com o mid
    // (enrichQuotedMetaMessages completa o body a partir do historico carregado).
    const replyMid = core.reply_to?.mid || outer.quotedMsgId;
    if (!replyMid) return {};

    const resolved = outer.quotedMsg;
    if (resolved && (resolved.body != null || resolved.mediaUrl || resolved.isStory)) {
        return {
            quotedMsgId: replyMid,
            quotedMsg: {
                id: resolved.id || replyMid,
                type: resolved.type || 'chat',
                body: resolved.body || '',
                caption: resolved.caption || '',
                fromMe: !!resolved.fromMe,
                mediaUrl: resolved.mediaUrl,
                isMetaMedia: resolved.isMetaMedia,
                mimetype: resolved.mimetype,
                isStory: resolved.isStory
            }
        };
    }

    return {
        quotedMsgId: replyMid,
        quotedMsg: {
            id: replyMid,
            type: 'chat',
            body: '',
            caption: '',
            fromMe: !!core.reply_to?.is_self_reply
        }
    };
}

export function mapMetaMessageToBubble(doc) {
    if (!doc) return null;

    const outer = doc.message || {};
    const core = outer.message || {};
    const attachments = core.attachments || outer.attachments || [];
    const att = Array.isArray(attachments) ? attachments[0] : null;
    const attType = att?.type;
    const attUrl = att?.payload?.url || null;

    const hasText = core.text != null && core.text !== '';
    const tsMs = Number(doc.timestamp || outer.timestamp || (doc.date ? new Date(doc.date).getTime() : 0)) || 0;
    // O balao formata a data com `new Date(m.timestamp * 1000)` — timestamp precisa
    // estar em SEGUNDOS (igual ao WhatsApp). Em ms a data ia parar no ano 58521.
    const tsSec = tsMs ? Math.floor(tsMs / 1000) : undefined;

    const quoteFields = buildQuotedFromReplyTo(core, outer);

    const base = {
        _id: doc._id,
        id: core.mid || String(doc._id || ''),
        // mid nativo da Meta — usar no reply_to (id pode cair no _id do Mongo se mid ausente)
        metaMid: core.mid || null,
        fromMe: !!outer.isMe,
        ack: outer.isRead ? 3 : 1,
        t: tsSec,
        timestamp: tsSec,
        date: doc.date,
        isDeleted: !!core.is_deleted,
        reaction: outer.reaction,
        platform: doc.platform || (doc.messengerUserId ? 'messenger' : 'instagram'),
        ...quoteFields,
    };

    // Anexo de midia suportado (imagem/video/audio/arquivo) — usa o mesmo balao do WhatsApp.
    // Se o `type` da Meta nao bater com o mapa (story_mention, share, reel...), tenta
    // pela extensao da URL antes de desistir e cair no link de texto.
    const media = (attType ? ATTACHMENT_TO_WHATSAPP[attType] : null) || mediaFromUrlExtension(attUrl);
    if (media && attUrl) {
        return {
            ...base,
            type: media.type,
            mimetype: media.mimetype,
            body: '',
            caption: hasText ? core.text : '',
            // Flags lidas pelo ChatComponent para renderizar a partir da URL (sem cache/base64):
            isMetaMedia: true,
            mediaUrl: attUrl,
            // ChatComponent usa para mostrar o rotulo "Mencionou voce em um story"
            // acima da midia — sem isso o video/imagem aparece identico a um envio normal.
            isStoryMention: attType === 'story_mention',
        };
    }

    // Sobrou aqui so o que nem o `type` nem a extensao da URL identificam como midia
    // (ex.: "share" apontando pra uma pagina do Instagram) — vira um link de texto.
    if (attType && attUrl) {
        return { ...base, type: 'chat', body: attUrl, caption: '' };
    }

    // Texto puro. `caption` fica vazia de proposito: o balao renderiza body E caption,
    // entao preencher os dois mostrava o mesmo texto duas vezes.
    return {
        ...base,
        type: 'chat',
        body: hasText ? core.text : '',
        caption: '',
    };
}

/**
 * Completa quotedMsg vazios buscando a mensagem original no mesmo lote (ou pool
 * extra, ex.: store.mensagens no socket). Necessario para historico antigo salvo
 * sem quotedMsg resolvido — so tinha reply_to.mid.
 */
export function enrichQuotedMetaMessages(mappedMessages, extraPool = []) {
    if (!Array.isArray(mappedMessages) || mappedMessages.length === 0) return mappedMessages;

    const pool = [...mappedMessages, ...(Array.isArray(extraPool) ? extraPool : [])];
    const byId = new Map();
    for (const m of pool) {
        if (m?.id) byId.set(String(m.id), m);
    }

    return mappedMessages.map((msg) => {
        if (!msg?.quotedMsgId) return msg;
        const hasContent = msg.quotedMsg && (
            (msg.quotedMsg.body != null && msg.quotedMsg.body !== '') ||
            msg.quotedMsg.mediaUrl ||
            msg.quotedMsg.isStory
        );
        if (hasContent) return msg;

        const original = byId.get(String(msg.quotedMsgId));
        if (!original) return msg;

        return {
            ...msg,
            quotedMsg: {
                id: original.id,
                type: original.type || 'chat',
                body: original.body || '',
                caption: original.caption || '',
                fromMe: !!original.fromMe,
                mediaUrl: original.mediaUrl,
                isMetaMedia: original.isMetaMedia,
                mimetype: original.mimetype,
            }
        };
    });
}

export default mapMetaMessageToBubble;
